import * as React from 'react';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { paletteState } from 'core/state';
import { isEditableTarget } from 'core/utils/isEditableTarget';
import { isGoToArmed } from 'core/utils/goToSequence';
import { matchesAltLetter } from 'core/utils/altHotkey';

import { PageShortcutsContext } from './PageShortcutsContext';
import { IPageAction, PageActionGetter } from './types';

/**
 * Holds the registry of per-page contextual shortcuts and owns the single global
 * keydown dispatcher for them. Mounted high enough (in `RootRouter`) to wrap both
 * the pages that register shortcuts AND the command palette that renders the
 * cheat sheet, so the sheet can read the live registry.
 *
 * Letter page actions fire on `Alt`+letter (Option+letter on macOS) — a modifier
 * chord that works even while typing in a field, and never collides with the
 * browser-reserved `Ctrl`/`Cmd` keys. Non-letter keys (e.g. `'/'`) stay plain and
 * fire only when the operator is NOT typing. Either way a key is ignored while a
 * `g` chord is pending or the palette / cheat sheet is open. When two mounted
 * pages register the same key, the most recently mounted one wins (topmost
 * context).
 */
export const PageShortcutsProvider = memo<{ children: React.ReactNode }>(
  ({ children }) => {
    // Insertion-ordered so "last registered wins" on a key clash.
    const registryRef = useRef<Map<symbol, PageActionGetter>>(new Map());
    const [version, setVersion] = useState(0);

    const register = useCallback((id: symbol, getter: PageActionGetter) => {
      registryRef.current.set(id, getter);
      setVersion((value) => value + 1);
    }, []);

    const unregister = useCallback((id: symbol) => {
      registryRef.current.delete(id);
      setVersion((value) => value + 1);
    }, []);

    const getShortcuts = useCallback((): IPageAction[] => {
      const flat: IPageAction[] = [];
      registryRef.current.forEach((getter) => {
        getter().forEach((shortcut) => flat.push(shortcut));
      });
      return flat;
    }, []);

    useEffect(() => {
      if (typeof document === 'undefined') {
        return undefined;
      }

      const onKeyDown = (event: KeyboardEvent) => {
        if (event.defaultPrevented) {
          return;
        }
        // `Ctrl`/`Cmd` chords are never page actions; `Alt` IS the page-action
        // modifier now, so it must NOT short-circuit here.
        if (event.metaKey || event.ctrlKey) {
          return;
        }
        if (paletteState().open || isGoToArmed()) {
          return;
        }

        const shortcuts = getShortcuts().filter(
          (shortcut) => shortcut.when !== false && Boolean(shortcut.key),
        );

        // `Alt`+letter page actions fire even while a field is focused — a
        // modifier chord is unambiguous, so typing is not a reason to stand down.
        const altMatches = shortcuts.filter((shortcut) =>
          matchesAltLetter(event, shortcut.key as string),
        );
        const altTarget = altMatches[altMatches.length - 1];
        if (altTarget) {
          event.preventDefault();
          altTarget.run();
          return;
        }

        // Plain non-letter keys (e.g. `'/'`) fire only when NOT typing in a field.
        if (isEditableTarget(event.target)) {
          return;
        }
        const plainMatches = shortcuts.filter(
          (shortcut) =>
            !event.altKey &&
            !/^[a-z]$/.test(shortcut.key as string) &&
            event.key === shortcut.key,
        );
        const plainTarget = plainMatches[plainMatches.length - 1];
        if (plainTarget) {
          event.preventDefault();
          plainTarget.run();
        }
      };

      document.addEventListener('keydown', onKeyDown);
      return () => document.removeEventListener('keydown', onKeyDown);
    }, [getShortcuts]);

    const value = useMemo(
      () => ({ register, unregister, getShortcuts, version }),
      [register, unregister, getShortcuts, version],
    );

    return (
      <PageShortcutsContext.Provider value={value}>
        {children}
      </PageShortcutsContext.Provider>
    );
  },
);

PageShortcutsProvider.displayName = 'PageShortcutsProvider';
